import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AssistantKey, getAssistant$, TestCase, TestType } from '@vendasta/protos/ai_assistants/v1alpha1';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { TestManagementService } from '../test-management.service';
import { TestCaseEditModalComponent } from '../test-case-edit-modal/test-case-edit-modal.component';
import { AssistantNamePipe } from '../../../core/utils/namespace-utils';
import { ButtonLoadingIndicatorComponent } from '@vendasta/galaxy/button-loading-indicator';
import { ButtonGroupComponent } from '@vendasta/galaxy/button-group';
import { TranslateService } from '@ngx-translate/core';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { AssistantKeyResolverService } from '../../../core/services/assistant-key-resolver.service';
import { snackbarDuration } from '../test-run.utils';

@Component({
  selector: 'ai-generate-test-cases',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTableModule,
    GalaxyAlertModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    GalaxyPageModule,
    GalaxySnackbarModule,
    GalaxyEmptyStateModule,
    AssistantNamePipe,
    ButtonLoadingIndicatorComponent,
    ButtonGroupComponent,
    AiAssistantI18nModule,
  ],

  providers: [AssistantKeyResolverService],
  templateUrl: './generate-test-cases.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './generate-test-cases.component.scss',
})
export class GenerateTestCasesComponent implements OnInit {
  private readonly testService = inject(TestManagementService);
  private readonly translateService = inject(TranslateService);
  private readonly snackbar = inject(SnackbarService);
  private readonly dialog = inject(MatDialog);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly assistantKeyResolver = inject(AssistantKeyResolverService);

  instructionsControl = new FormControl('', { nonNullable: true });
  generatedCases: TestCase[] = [];
  generating = false;
  saving = false;
  hasGenerated = false;
  assistantKey: AssistantKey;
  testType: TestType = TestType.TEST_TYPE_CHAT;

  get isVoice(): boolean {
    return this.testType === TestType.TEST_TYPE_VOICE;
  }

  displayedColumns = ['name', 'prompt', 'expectation', 'edit', 'remove'];

  private assistantKey$ = this.assistantKeyResolver.resolveFromRoute(this.activatedRoute);

  protected assistant$ = this.assistantKey$.pipe(
    switchMap((key) => getAssistant$({ id: key.id, namespace: key.namespace })),
    map((resp) => resp.assistant),
    shareReplay(1),
  );

  ngOnInit(): void {
    const testTypeParam = this.activatedRoute.snapshot.queryParamMap.get('testType');
    this.testType = testTypeParam === 'voice' ? TestType.TEST_TYPE_VOICE : TestType.TEST_TYPE_CHAT;

    this.assistantKey$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((key) => {
      this.assistantKey = key;
    });
  }

  generate(): void {
    if (this.generating) return;

    this.generating = true;
    this.generatedCases = [];

    this.testService
      .generateTestCases(this.assistantKey, this.instructionsControl.value, this.testType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.generatedCases = resp.testCases || [];
          this.generating = false;
          this.hasGenerated = true;
        },
        error: (err) => {
          const message =
            err.error?.message || this.translateService.instant('AI_ASSISTANT.TESTING.GENERATE.ERROR_GENERATING');
          this.snackbar.openErrorSnack(message, { duration: snackbarDuration });
          this.generating = false;
        },
      });
  }

  editCase(index: number): void {
    const testCase = this.generatedCases[index];
    this.dialog
      .open(TestCaseEditModalComponent, { data: { testCase, testType: this.testType } })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: TestCase) => {
        if (result) {
          this.generatedCases = this.generatedCases.map((c, i) => (i === index ? result : c));
        }
      });
  }

  removeCase(index: number): void {
    this.generatedCases = this.generatedCases.filter((_, i) => i !== index);
  }

  saveAll(): void {
    if (this.generatedCases.length === 0 || this.saving) return;

    this.saving = true;
    this.testService
      .upsertTestCases(this.assistantKey, this.generatedCases)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackbar.openSuccessSnack(this.translateService.instant('AI_ASSISTANT.TESTING.GENERATE.SAVED'), {
            duration: snackbarDuration,
          });
          this.saving = false;
          this.router.navigate(['..'], { relativeTo: this.activatedRoute });
        },
        error: (err) => {
          const message =
            err.error?.message || this.translateService.instant('AI_ASSISTANT.TESTING.GENERATE.ERROR_SAVING');
          this.snackbar.openErrorSnack(message, { duration: snackbarDuration });
          this.saving = false;
        },
      });
  }

  goBack(): void {
    this.router.navigate(['..'], { relativeTo: this.activatedRoute });
  }
}
